import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { notification, Row, Col, Button, Select, Form, DatePicker, Input, Modal, Typography, Tooltip } from 'antd';
import { OBA, PAPER_SHEEN, PAPER_WEIGHT, PRESENCE_OF_SELENIUM } from '../../../config';
import {
  intialPaginationState,
  formItemLayout,
  tailFormItemLayout,
  unexpectedError,
  onlyNumbers,
  onlyNumber,
  validatePair,
} from '../../../common_functions';
import AdvanceNegativeSearch from '../../negativeFilter/AdvanceModal';
import * as userActions from '../../../redux/actions/userActions';
import { useDispatch } from 'react-redux';
import { ReloadOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
const { RangePicker } = DatePicker;

const AdvanceFilter = ({ visible, onClose, props, resetDataState, resetData, fetchReports: getDataReports }) => {
  const formRef = useRef(null);
  const formNegRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [conceptPortfolios, setConceptPortfolios] = useState([]);
  const [filteredStampData, setFilteredStampData] = useState([]);
  const [childStamps, setChildStamps] = useState([]);
  const [handWritingNotes, setHandWritingNotes] = useState([]);
  const [paperGroups, setPaperGroups] = useState([]);

  const [negatives, setNegatives] = useState([]);
  const [negFormVisible, setNegFormVisible] = useState(false);
  const [selectedNegative, setSelectedNegative] = useState(null);

  //Get collection data
  useEffect(() => {
    const getDefaultData = async () => {
      try {
        setLoading(true);
        const sData = await getStamps();
        setFilteredStampData(sData);
        await getHandWritingNotes();
        await getConcetualPortfolio();
        await getPaperGroups();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    getDefaultData();
  }, []);
  useEffect(() => {
    let isMounted = true;

    (async () => {
      if (!resetData) return;
      setLoading(true);
      await getDataReports(intialPaginationState, { object_type: { _eq: 'print' } });

      if (isMounted) {
        resetDataState(false);
        formRef.current?.resetFields();
        formNegRef.current?.resetFields();
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [resetData]);

  useEffect(() => {
    formRef?.current?.setFieldsValue({ negative: selectedNegative });
  }, [selectedNegative]);

  const getHandWritingNotes = async () => {
    const { data } = await dispatch(
      userActions.getItems('hand_writing_notes', {
        fields: ['id', 'name'],
        sort: 'name',
        limit: -1,
      })
    );
    setHandWritingNotes(data);
  };
  const getPaperGroups = async () => {
    const { data } = await dispatch(
      userActions.getItems('paper_groups', {
        fields: ['id,name'],
        sort: 'name',
        limit: -1,
      })
    );
    setPaperGroups(data);
  };
  const getStamps = async (isSubgroup = false, parentStamp) => {
    const { data } = await dispatch(
      userActions.getItems('stamps', {
        fields: ['id', 'stamp_name', 'height', 'width'],
        sort: 'stamp_name',
        limit: -1,
        filters: parentStamp
          ? { parent_stamp: { _eq: parentStamp }, is_subgroup: { _eq: isSubgroup } }
          : { is_subgroup: { _eq: isSubgroup } },
      })
    );
    return data;
  };
  const showSubgroup = async parentStamp => {
    let data = await getStamps(true, parentStamp);
    if (data?.length > 0) {
      formRef.current.setFieldsValue({ stamp_subgroup: null });
    }
    setChildStamps(data);
  };

  const getConcetualPortfolio = async () => {
    const { data: portfolio } = await dispatch(
      userActions.getItems('portfolio', {
        fields: ['id', 'name'],
        limit: '-1',
        sort: 'name',
        filters: { type: { _eq: process.env.conceptual_work_id } },
      })
    );
    setConceptPortfolios(portfolio);
  };

  const setFilters = values => {
    let filters = { object_type: { _eq: 'print' } };
    let conditions = [];
    const addCondition = condition => condition && conditions.push(condition);
    // Helper function to format date
    const formatDate = date => dayjs(date).startOf('year').toISOString();

    // Date Range Filters
    if (values?.print_date_range) {
      const [start, end] = values.print_date_range;
      addCondition({ print: { creation_date_start_year: { _gte: formatDate(start) } } });
      addCondition({ print: { creation_date_end_year: { _lte: formatDate(end) } } });
    }

    if (values?.negative_date_range) {
      const [start, end] = values.negative_date_range;
      addCondition({ print: { negative_id: { creation_earliest: { _gte: formatDate(start) } } } });
      addCondition({ print: { negative_id: { creation_latest: { _lte: formatDate(end) } } } });
    }

    if (values?.image_dimension_height) {
      const height = Number(values.image_dimension_height);
      const range = Number(values.image_dimension_height_range) || 0;
      const minHeight = Math.max(0, height - range);
      const maxHeight = height + range;

      addCondition({ print: { image_dimension_height: { _gte: minHeight } } });
      addCondition({ print: { image_dimension_height: { _lte: maxHeight } } });
    }
    if (values?.image_dimension_width) {
      const width = Number(values.image_dimension_width);
      const range = Number(values.image_dimension_width_range) || 0;
      const minWidth = Math.max(0, width - range);
      const maxWidth = width + range;
      addCondition({ print: { image_dimension_width: { _gte: minWidth } } });
      addCondition({ print: { image_dimension_width: { _lte: maxWidth } } });
    }
    if (values?.ba_sr) {
      const baSrAvg = Number(values.ba_sr);
      const range = Number(values.ba_sr_range) || 0;
      const minBaSRAvg = Math.max(0, baSrAvg - range);
      const maxBaSRAvg = baSrAvg + range;
      addCondition({ print: { xrf: { avg_dmin_ba_sr: { _gte: minBaSRAvg } } } });
      addCondition({ print: { xrf: { avg_dmin_ba_sr: { _lte: maxBaSRAvg } } } });
    }

    // General Filters
    const mappings = [
      { key: 'print', field: 'portfolio_add_number', value: values?.portfolio },
      { key: 'print', field: 'stamp', value: values?.stamp_subgroup ?? values?.stamp },
      { key: 'paper_group', field: 'id', value: values?.paper_group },
      { key: 'print', field: 'negative_id', subField: 'id', value: values?.negative },
      { key: 'print', field: 'photo_spectro_meter', subField: 'oba', value: values?.oba },
      { key: 'print', field: 'xrf', subField: 'presence_of_selenium_se', value: values?.xrf_se },
      { key: 'print', field: 'glossmeter', subField: 'paper_sheen', value: values?.gloss },
      { key: 'print', field: 'micrometer', subField: 'paper_weight', value: values?.paper_weight },
    ];

    mappings.forEach(({ key, field, subField, value }) => {
      if (value) {
        addCondition(subField ? { [key]: { [field]: { [subField]: { _eq: value } } } } : { [key]: { [field]: { _eq: value } } });
      }
    });
    // Mount Thickness
    if (values?.mount_thickness) {
      const mountThickness = Number(values.mount_thickness);
      const range = Number(values.mount_thickness_range) || 0;
      const minMountThickness = Math.max(0, mountThickness - range);
      const maxMountThickness = mountThickness + range;
      addCondition({
        print: {
          mounting_method: { _eq: process.env.STANDARD_MOUNT },
        },
      });
      addCondition({
        print: {
          micrometer: {
            thickness: { _gte: minMountThickness },
          },
        },
      });
      addCondition({
        print: {
          micrometer: {
            thickness: { _lte: maxMountThickness },
          },
        },
      });
    }

    // Annotation Filters
    ['annotation_recto_handwriting', 'annotation_verso_handwriting', 'stamp_verso'].forEach(key => {
      if (values?.[key]) {
        addCondition({
          print: {
            basic_photos: {
              [key.includes('verso') ? (key.includes('stamp') ? 'stamp_verso' : 'annotations_verso') : 'annotations_recto']: {
                hand_writing_notes: { _eq: values[key] },
              },
            },
          },
        });
      }
    });

    return { _and: [filters, ...conditions] };
  };
  const resetFilters = async () => {
    formRef.current?.resetFields();
    try {
      await getDataReports(intialPaginationState, { object_type: { _eq: 'print' } });
      notification.success({ message: 'Search filters are reset Successfully!', duration: 2 });
    } catch (error) {
      console.log(error);
      unexpectedError(error);
    }
  };
  const onFinish = async values => {
    try {
      setLoading(true);
      await getDataReports(intialPaginationState, setFilters(values));
      notification.success({ message: 'Results updated based on your search.', duration: 2 });
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const closeNegativeModal = () => {
    setNegFormVisible(false);
  };

  return (
    <>
      <Modal title="Advanced Data Filters" open={visible} onCancel={onClose} footer={false} width={1200} confirmLoading={loading}>
        <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
          <Row gutter={24}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="negative"
                className="labelLink"
                label={
                  <div
                    style={{
                      justifyContent: 'space-between',
                      width: '100%',
                      textAlign: 'center',
                      display: 'flex',
                    }}
                  >
                    Image/Negative
                    <Tooltip placement="rightTop" title="Advance Data reporting">
                      <a style={{ color: '#a6a300', cusrsor: 'pointer' }} onClick={() => setNegFormVisible(true)}>
                        Advance filter
                      </a>
                    </Tooltip>
                  </div>
                }
              >
                <Select
                  placeholder="Select Image/Negative"
                  optionFilterProp="children"
                  showSearch
                  allowClear
                  filterOption={(input, option) => option.children.includes(input)}
                  onChange={e => {
                    setSelectedNegative(e);
                  }}
                >
                  {negatives?.map(({ negative: { id, obj_name } }) => (
                    <Select.Option key={id} value={id}>
                      {obj_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="negative_date_range" label="Negative Date Range">
                <RangePicker picker="year" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="print_date_range" label="Print Date Range">
                <RangePicker picker="year" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="image_dimension_height"
                label="Image Dimension Height (mm)"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'image_dimension_height_range',
                    'Both fields, "Image Dimension Height (mm)" and "+/- Image Dimension Height (mm)" are required if either one is filled!'
                  ),
                ]}
              >
                <Input onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="image_dimension_height_range"
                label="+/- Image Dimension Height (mm)"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'image_dimension_height',
                    'Both fields, "Image Dimension Height (mm)" and "+/- Image Dimension Height (mm)" are required if either one is filled!'
                  ),
                ]}
              >
                <Input onKeyPress={onlyNumbers} placeholder="+/- height" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="image_dimension_width"
                label="Image Dimension Width (mm)"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'image_dimension_width_range',
                    'Both fields, "Image Dimension Width (mm)" and "+/- Image Dimension Width (mm)" are required if either one is filled!'
                  ),
                ]}
              >
                <Input onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="image_dimension_width_range"
                label="+/- Image Dimension Width (mm)"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'image_dimension_width',
                    'Both fields, "Image Dimension Width  (mm)" and "+/- Image Dimension Width (mm)" are required if either one is filled!'
                  ),
                ]}
              >
                <Input onKeyPress={onlyNumbers} placeholder="+/- width" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="portfolio" label="Portfolio">
                <Select placeholder="Select Portfolio" optionFilterProp="children" showSearch allowClear>
                  {conceptPortfolios.map(col => {
                    return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="stamp" label="Stamp">
                <Select
                  showSearch
                  allowClear
                  virtual={false}
                  filterOption={false}
                  placeholder="Select stamp"
                  style={{ textTransform: 'capitalize' }}
                  onChange={showSubgroup}
                >
                  {filteredStampData?.map(el => (
                    <Select.Option key={el.id} value={el.id}>
                      {el.stamp_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {childStamps?.length > 0 && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Form.Item name="stamp_subgroup" label="Stamp Subgroup">
                  <Select
                    showSearch
                    allowClear
                    virtual={false}
                    filterOption={false}
                    placeholder="Select stamp"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {childStamps?.map(el => (
                      <Select.Option key={el.id} value={el.id} onMouseEnter={() => {}} onMouseLeave={() => {}}>
                        {el.stamp_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="stamp_verso" label="Stamp Handwriting">
                <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                  {handWritingNotes.map(col => {
                    return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="annotation_recto_handwriting" label="Annotation Recto Handwriting">
                <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                  {handWritingNotes.map(col => {
                    return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="annotation_verso_handwriting" label="Annoation Verso Handwriting">
                <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                  {handWritingNotes.map(col => {
                    return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="oba" label="OBA">
                <Select placeholder="Select OBA" showSearch allowClear optionFilterProp="children">
                  {OBA.map(col => {
                    return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="xrf_se" label="Se">
                <Select placeholder="Select SE value" showSearch allowClear optionFilterProp="children">
                  {PRESENCE_OF_SELENIUM.map(col => {
                    return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="gloss" label="Gloss">
                <Select placeholder="Select gloss type" showSearch allowClear optionFilterProp="children">
                  {PAPER_SHEEN.map(col => {
                    return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="ba_sr"
                label="Ba/Sr"
                rules={[
                  { validator: onlyNumber },
                  validatePair('ba_sr_range', 'Both fields, "Ba/Sr" and "+/- Ba/Sr" are required if either one is filled!'),
                ]}
              >
                <Input placeholder="Ba/Sr" onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="ba_sr_range"
                label="+/- Ba/Sr"
                rules={[
                  { validator: onlyNumber },
                  validatePair('ba_sr', 'Both fields, "Ba/Sr" and "+/- Ba/Sr" are required if either one is filled!'),
                ]}
              >
                <Input placeholder="+/- Ba/Sr" onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="paper_weight" label="Paper Weight">
                <Select placeholder="Select gloss type" showSearch allowClear optionFilterProp="children">
                  {PAPER_WEIGHT.map(col => {
                    return <Select.Option key={col.key} value={col.key}>{col.value}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="mount_thickness"
                label="Mount Thickness"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'mount_thickness_range',
                    'Both fields, "Mount Thickness" and "+/- Mount Thickness" are required if either one is filled!'
                  ),
                ]}
              >
                <Input placeholder="Mount thickness" onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                name="mount_thickness_range"
                label="+/- Mount Thickness"
                rules={[
                  { validator: onlyNumber },
                  validatePair(
                    'mount_thickness',
                    'Both fields, "Mount Thickness" and "+/- Mount Thickness" are required if either one is filled!'
                  ),
                ]}
              >
                <Input placeholder="+/- Mount Thickness" onKeyPress={onlyNumbers} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="paper_group" label="Paper Group">
                <Select placeholder="Select report" showSearch allowClear optionFilterProp="children">
                  {paperGroups.map(col => {
                    return <Select.Option key={col.id} value={col.id}>{col.name}</Select.Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item style={{ float: 'right' }}>
                <Button
                  style={{
                    marginRight: '10px',
                    height: '42px',
                    marginTop: '28px',
                  }}
                  size="small"
                  danger
                  onClick={resetFilters}
                  icon={<ReloadOutlined />}
                >
                  Reset Filters
                </Button>

                <Button
                  type="primary"
                  size="small"
                  htmlType="submit"
                  style={{
                    marginTop: '28px',
                    height: '42px',
                  }}
                  icon={<SearchOutlined />}
                >
                  Search
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <AdvanceNegativeSearch
        visible={negFormVisible}
        onClose={closeNegativeModal}
        props={props}
        selectedNegative={selectedNegative}
        setSelectedNegative={setSelectedNegative}
        setNegatives={setNegatives}
        reportType="Data"
      />
    </>
  );
};
export default AdvanceFilter;
